import {
  TUNNEL_NAME_MAX_LENGTH,
  TUNNEL_PULSE_MS_DEFAULT,
  TUNNEL_PULSE_MS_MAX,
  TUNNEL_PULSE_MS_MIN,
  TUNNEL_RELAY_MAX,
  TUNNEL_RELAY_MIN,
  TUNNEL_SERIAL_PATTERN,
  TUNNEL_TRIGGER_MODE_OPTIONS,
} from '../constants/tunnel.constants';
import {
  Tunnel,
  TunnelConnectionDisplay,
  TunnelFormValues,
  TunnelPackage,
  TunnelPackageFormValues,
  TunnelPackagePayload,
  TunnelPayload,
  TunnelSettings,
  TunnelStatus,
  TunnelTriggerMode,
} from '../models/tunnel.model';

type Raw = Record<string, unknown>;

function asId(value: unknown): string {
  return value === null || value === undefined ? '' : String(value);
}

function asNullableId(value: unknown): string | null {
  const id = asId(value).trim();
  return id ? id : null;
}

function asNumber(value: unknown, fallback: number): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function asStatus(value: unknown): TunnelStatus {
  return value === 'inactive' ? 'inactive' : 'active';
}

function asTriggerMode(value: unknown): TunnelTriggerMode {
  return value === 'entrance_read' ? 'entrance_read' : 'order_sent';
}

export function mapTunnelFromApi(raw: unknown): Tunnel | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const t = raw as Raw;
  return {
    id: asId(t['id']),
    name: String(t['name'] ?? ''),
    serial: String(t['serial'] ?? ''),
    trigger_mode: asTriggerMode(t['trigger_mode']),
    status: asStatus(t['status']),
    sse_last_connected_at: t['sse_last_connected_at'] ? String(t['sse_last_connected_at']) : null,
  };
}

export function mapTunnelPackageFromApi(raw: Raw): TunnelPackage {
  return {
    id: asId(raw['id']),
    name: String(raw['name'] ?? ''),
    relay: asNumber(raw['relay'], 0),
    pulse_ms: asNumber(raw['pulse_ms'], TUNNEL_PULSE_MS_DEFAULT),
    status: asStatus(raw['status']),
    package_count: asNumber(raw['package_count'], 0),
  };
}

/** Normalises the GET payload; tolerant of a missing or partial body. */
export function mapTunnelSettingsFromApi(data: unknown): TunnelSettings {
  const d = (data && typeof data === 'object' ? data : {}) as Raw;
  const packages = Array.isArray(d['tunnel_packages'])
    ? (d['tunnel_packages'] as Raw[]).map(mapTunnelPackageFromApi)
    : [];
  const counts = (d['counts'] && typeof d['counts'] === 'object' ? d['counts'] : {}) as Raw;
  return {
    tunnel: mapTunnelFromApi(d['tunnel']),
    tunnel_packages: packages,
    counts: {
      active_tunnel_packages: asNumber(
        counts['active_tunnel_packages'],
        countActiveTunnelPackages(packages)
      ),
    },
  };
}

export function countActiveTunnelPackages(packages: Pick<TunnelPackage, 'status'>[]): number {
  return packages.filter(p => p.status === 'active').length;
}

export function getTunnelTriggerModeLabel(mode: TunnelTriggerMode): string {
  return TUNNEL_TRIGGER_MODE_OPTIONS.find(o => o.value === mode)?.label ?? mode;
}

export function getTunnelTriggerModeShortLabel(mode: TunnelTriggerMode): string {
  return TUNNEL_TRIGGER_MODE_OPTIONS.find(o => o.value === mode)?.shortLabel ?? mode;
}

/**
 * Connection line for the tunnel card. `sse_last_connected_at` is stamped when the
 * Tunnel Controller opens its command stream, so it tells "has connected, last at…",
 * not whether the stream is still open right now.
 */
export function describeTunnelConnection(
  lastConnectedAt: string | null | undefined,
  formatDate: (date: Date) => string = date => date.toLocaleString()
): TunnelConnectionDisplay {
  const ms = lastConnectedAt ? Date.parse(lastConnectedAt) : NaN;
  if (!Number.isFinite(ms)) {
    return { state: 'never', label: 'Never connected' };
  }
  return { state: 'last_seen', label: `Last connected ${formatDate(new Date(ms))}` };
}

export function normalizeTunnelSerial(serial: string): string {
  return serial.trim().toUpperCase();
}

export interface TunnelFormErrors {
  name: string;
  serial: string;
}

export function validateTunnelForm(values: TunnelFormValues): TunnelFormErrors {
  const name = values.name.trim();
  const serial = normalizeTunnelSerial(values.serial);
  let nameError = '';
  if (!name) {
    nameError = 'Tunnel name is required';
  } else if (name.length > TUNNEL_NAME_MAX_LENGTH) {
    nameError = `Use ${TUNNEL_NAME_MAX_LENGTH} characters or fewer`;
  }
  let serialError = '';
  if (!serial) {
    serialError = 'Device serial number is required';
  } else if (!TUNNEL_SERIAL_PATTERN.test(serial)) {
    serialError = 'Use 6 to 32 letters and digits (e.g. E827252CF9B1)';
  }
  return { name: nameError, serial: serialError };
}

export function buildTunnelPayload(values: TunnelFormValues): TunnelPayload {
  return {
    name: values.name.trim(),
    serial: normalizeTunnelSerial(values.serial),
    trigger_mode: values.triggerMode,
  };
}

export interface TunnelPackageFormErrors {
  name: string;
  relay: string;
  pulseMs: string;
}

function parseWholeNumber(value: string): number | null {
  const trimmed = String(value ?? '').trim();
  if (!/^\d+$/.test(trimmed)) {
    return null;
  }
  return Number(trimmed);
}

/**
 * Validates a tunnel wash package form against the API rules, including the
 * "unique active name and relay per tunnel" constraint, so the admin sees the
 * clash before the request.
 */
export function validateTunnelPackageForm(
  values: TunnelPackageFormValues,
  existing: TunnelPackage[],
  editingId: string | null
): TunnelPackageFormErrors {
  const errors: TunnelPackageFormErrors = { name: '', relay: '', pulseMs: '' };
  const others = existing.filter(p => p.status === 'active' && p.id !== editingId);

  const name = values.name.trim();
  if (!name) {
    errors.name = 'Name is required';
  } else if (name.length > TUNNEL_NAME_MAX_LENGTH) {
    errors.name = `Use ${TUNNEL_NAME_MAX_LENGTH} characters or fewer`;
  } else if (others.some(p => p.name.trim().toLowerCase() === name.toLowerCase())) {
    errors.name = 'Another tunnel wash package already has this name';
  }

  const relay = parseWholeNumber(values.relay);
  if (relay === null || relay < TUNNEL_RELAY_MIN || relay > TUNNEL_RELAY_MAX) {
    errors.relay = `Enter a c-relay port from ${TUNNEL_RELAY_MIN} to ${TUNNEL_RELAY_MAX}`;
  } else {
    const clash = others.find(p => p.relay === relay);
    if (clash) {
      errors.relay = `C-relay ${relay} is already used by ${clash.name}`;
    }
  }

  const pulse = parseWholeNumber(values.pulseMs);
  if (pulse === null || pulse < TUNNEL_PULSE_MS_MIN || pulse > TUNNEL_PULSE_MS_MAX) {
    errors.pulseMs = `Enter a pulse from ${TUNNEL_PULSE_MS_MIN} to ${TUNNEL_PULSE_MS_MAX} ms`;
  }
  return errors;
}

export function hasFormErrors(errors: object): boolean {
  return Object.values(errors).some(Boolean);
}

export function buildTunnelPackagePayload(values: TunnelPackageFormValues): TunnelPackagePayload {
  return {
    name: values.name.trim(),
    relay: Number(values.relay.trim()),
    pulse_ms: Number(values.pulseMs.trim()),
  };
}

export function mapTunnelPackageToForm(pkg: TunnelPackage | null): TunnelPackageFormValues {
  return {
    name: pkg?.name ?? '',
    relay: pkg ? String(pkg.relay) : '',
    pulseMs: String(pkg?.pulse_ms ?? TUNNEL_PULSE_MS_DEFAULT),
  };
}

/** First API error message, falling back to `fallback`. */
export function apiErrorMessage(err: unknown, fallback: string): string {
  const e = err as { error?: { message?: unknown } } | null;
  const message = e?.error?.message;
  return typeof message === 'string' && message.trim() ? message : fallback;
}

export function tunnelDeleteMessage(tunnel: Pick<Tunnel, 'name'>): string {
  return `Delete tunnel ${tunnel.name}? Nothing will fire any more: every wash at this location must be started manually.`;
}

export function tunnelPackageDeleteMessage(
  pkg: Pick<TunnelPackage, 'name' | 'package_count'>
): string {
  if (pkg.package_count <= 0) {
    return `Delete tunnel wash package ${pkg.name}? It will no longer fire.`;
  }
  const packages = pkg.package_count === 1 ? '1 package' : `${pkg.package_count} packages`;
  return `Delete tunnel wash package ${pkg.name}? ${packages} using it will switch to Manual: their tunnel wash is started by hand.`;
}
